import { useEffect, useRef } from 'react';

export interface ConfirmDialogProps {
  /** The question, e.g. "Quit game?". */
  title: string;
  /** What is lost, in a sentence; omitted when the action speaks for itself. */
  detail?: string;
  /** Label of the button that goes ahead; it carries the danger styling. */
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * A confirmation over the menu, for the few actions that throw a game away.
 *
 * Cancel takes the focus so the dangerous button is never the one a stray Return presses, and
 * Escape cancels — the same key that opened the menu in the first place.
 */
export function ConfirmDialog({
  title,
  detail,
  confirmLabel,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // The menu's own Escape handler would close the whole card behind us.
      e.stopPropagation();
      e.preventDefault();
      onCancel();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  return (
    <div className="confirm" role="presentation" onClick={onCancel}>
      <div
        className="confirm-card"
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="menu-heading">{title}</h2>
        {detail !== undefined && <p className="hint">{detail}</p>}
        <div className="confirm-actions">
          <button ref={cancelRef} onClick={onCancel} data-testid="confirm-cancel">
            Cancel
          </button>
          <button className="danger" onClick={onConfirm} data-testid="confirm-ok">
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
